import { useState, useRef, useEffect } from 'react';
import {
  Stack,
  Text,
  Paper,
  TextInput,
  Button,
  Group,
  Avatar,
  ScrollArea,
  ActionIcon,
  Menu,
  Tabs,
  Textarea,
  LoadingOverlay,
  Badge,
  Divider,
  Switch,
  Alert
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useGroupDiscussion } from '../../hooks/useGroupDiscussion';
import { formatDate } from '../../utils/dateUtils';
import editIcon from '../../assets/icons/edit.svg';
import trashIcon from '../../assets/icons/trash.svg';
import dotsIcon from '../../assets/icons/dots.svg';
import pinIcon from '../../assets/icons/pin.svg';
import sendIcon from '../../assets/icons/send.svg';

interface Message {
  id: string;
  text: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  user_name?: string;
  user_avatar?: string;
}

interface Announcement {
  id: string;
  text: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
  user_name?: string;
  pinned: boolean;
}

interface GroupDiscussionProps {
  groupId: string;
  accessToken: string;
  userId: string;
}

export const GroupDiscussion = ({ groupId, accessToken, userId }: GroupDiscussionProps) => {
  const [activeTab, setActiveTab] = useState<string | null>('chat');
  const [newMessage, setNewMessage] = useState('');
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editedContent, setEditedContent] = useState('');
  const [editingAnnouncementId, setEditingAnnouncementId] = useState<string | null>(null);
  const [newAnnouncement, setNewAnnouncement] = useState('');
  const [newAnnouncementPinned, setNewAnnouncementPinned] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    messages: rawMessages,
    announcements: rawAnnouncements,
    loading,
    error,
    sendMessage,
    deleteMessage,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
  } = useGroupDiscussion(groupId, accessToken);

  // Ensure messages and announcements are always arrays
  const messages = Array.isArray(rawMessages) ? rawMessages : [];
  const announcements = Array.isArray(rawAnnouncements) ? rawAnnouncements : [];

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      await sendMessage(newMessage);
      setNewMessage('');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to send message',
        color: 'red',
      });
    }
  };



  const handleDeleteMessage = async (messageId: string) => {
    if (!confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteMessage(messageId);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete message',
        color: 'red',
      });
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.trim()) return;

    try {
      await createAnnouncement(newAnnouncement, newAnnouncementPinned);
      setNewAnnouncement('');
      setNewAnnouncementPinned(false);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create announcement',
        color: 'red',
      });
    }
  };

  const handleEditAnnouncement = async (announcementId: string, text: string, pinned: boolean) => {
    try {
      await updateAnnouncement(announcementId, text, pinned);
      setEditingAnnouncementId(null);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update announcement',
        color: 'red',
      });
    }
  };

  const handleDeleteAnnouncement = async (announcementId: string) => {
    if (!confirm('Are you sure you want to delete this announcement?')) return;

    try {
      await deleteAnnouncement(announcementId);
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete announcement',
        color: 'red',
      });
    }
  };

  if (error) {
    return (
      <Alert color="red" title="Error loading discussion">
        {error}
      </Alert>
    );
  }

  return (
    <Stack style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading} />

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="chat">
            Chat
          </Tabs.Tab>
          <Tabs.Tab value="announcements">
            Announcements {announcements.filter(a => a.pinned).length > 0 && (
              <Badge size="xs" ml={4}>
                {announcements.filter(a => a.pinned).length}
              </Badge>
            )}
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="chat">
          <Stack mt="md">
            <ScrollArea h={400}>
              {messages.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  No messages yet. Start the conversation!
                </Text>
              ) : (
                <Stack gap="md">
                  {messages.map((message) => (
                    <div key={message.id}>

                      <Group
                        align="flex-start"
                        gap="xs"
                        justify={message.user_id === userId ? 'flex-end' : 'flex-start'}
                      >
                        {message.user_id !== userId && (
                          <Avatar
                            src={message.user_avatar}
                            radius="xl"
                            size="sm"
                          >
                            {message.user_name?.charAt(0)}
                          </Avatar>
                        )}

                        <div
                          style={{
                            maxWidth: '70%',
                            alignSelf: message.user_id === userId ? 'flex-end' : 'flex-start',
                          }}
                        >
                          <Paper
                            p="sm"
                            withBorder
                            bg={message.user_id === userId ? 'blue.1' : 'gray.0'}
                            style={{
                              borderRadius: message.user_id === userId ?
                                '8px 0px 8px 8px' : '0px 8px 8px 8px',
                            }}
                          >
                            {message.user_id !== userId && (
                              <Text size="xs" fw={500} mb={4}>
                                {message.user_name}
                              </Text>
                            )}
                            <Text size="sm">{message.text}</Text>
                          </Paper>

                          <Group justify="apart" mb="xs">
                            <Text size="xs" color="dimmed">
                              {formatDate(message.created_at, 'time')}
                            </Text>

                            {message.user_id === userId && (
                              <Menu position="bottom-end">
                                <Menu.Target>
                                  <ActionIcon size="xs" variant="subtle">
                                    <img src={dotsIcon} alt="More" width={12} />
                                  </ActionIcon>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <Menu.Item
                                    color="red"
                                    leftSection={<img src={trashIcon} alt="Delete" width={14} />}
                                    onClick={() => handleDeleteMessage(message.id)}
                                  >
                                    Delete
                                  </Menu.Item>
                                </Menu.Dropdown>
                              </Menu>
                            )}
                          </Group>
                        </div>

                        {message.user_id === userId && (
                          <Avatar
                            src={message.user_avatar}
                            radius="xl"
                            size="sm"
                          >
                            {message.user_name?.charAt(0)}
                          </Avatar>
                        )}
                      </Group>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </Stack>
              )}
            </ScrollArea>

            <Group>
              <TextInput
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
                style={{ flex: 1 }}
              />
              <Button onClick={handleSendMessage} rightSection={<img src={sendIcon} alt="" width={16} />}>
                Send
              </Button>
            </Group>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="announcements">
          <Stack mt="md">
            {announcements.length === 0 ? (
              <Text c="dimmed" ta="center" py="xl">
                No announcements yet.
              </Text>
            ) : (
              <Stack gap="md">
                {announcements.map((announcement) => (
                  <div key={announcement.id}>
                    {editingAnnouncementId === announcement.id ? (
                      <Stack>
                        <Textarea
                          placeholder="Edit announcement..."
                          value={editedContent}
                          onChange={(e) => setEditedContent(e.currentTarget.value)}
                          minRows={3}
                        />
                        <Group justify="apart">
                          <Switch
                            label="Pin announcement"
                            checked={newAnnouncementPinned}
                            onChange={(e) => setNewAnnouncementPinned(e.currentTarget.checked)}
                          />
                          <Group>
                            <Button
                              variant="subtle"
                              onClick={() => setEditingAnnouncementId(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => handleEditAnnouncement(
                                announcement.id,
                                editedContent,
                                newAnnouncementPinned
                              )}
                            >
                              Save
                            </Button>
                          </Group>
                        </Group>
                      </Stack>
                    ) : (
                      <>
                        <Group justify="apart" mb="xs">
                          <Group>
                            <Badge
                              color="blue"
                              leftSection={<img src={pinIcon} alt="Pinned" width={10} />}
                            >
                              Pinned
                            </Badge>
                            <Text size="xs" color="dimmed">
                              Posted by {announcement.user_name} on {formatDate(announcement.created_at)}
                            </Text>
                          </Group>

                          {announcement.user_id === userId && (
                            <Menu position="bottom-end">
                              <Menu.Target>
                                <ActionIcon variant="subtle">
                                  <img src={dotsIcon} alt="More" width={14} />
                                </ActionIcon>
                              </Menu.Target>
                              <Menu.Dropdown>
                                <Menu.Item
                                  leftSection={<img src={editIcon} alt="Edit" width={14} />}
                                  onClick={() => {
                                    setEditingAnnouncementId(announcement.id);
                                    setEditedContent(announcement.text);
                                    setNewAnnouncementPinned(announcement.pinned);
                                  }}
                                >
                                  Edit
                                </Menu.Item>
                                <Menu.Item
                                  leftSection={<img src={pinIcon} alt="Unpin" width={14} />}
                                  onClick={() => handleEditAnnouncement(
                                    announcement.id,
                                    announcement.text,
                                    false
                                  )}
                                >
                                  Unpin
                                </Menu.Item>
                                <Menu.Item
                                  color="red"
                                  leftSection={<img src={trashIcon} alt="Delete" width={14} />}
                                  onClick={() => handleDeleteAnnouncement(announcement.id)}
                                >
                                  Delete
                                </Menu.Item>
                              </Menu.Dropdown>
                            </Menu>
                          )}
                        </Group>
                        <Text>{announcement.text}</Text>
                      </>
                    )}
                  </div>
                ))}
              </Stack>
            )}

            <Paper withBorder p="md">
              <Stack>
                <Textarea
                  label="New Announcement"
                  placeholder="Share something important with the group..."
                  minRows={3}
                  value={newAnnouncement}
                  onChange={(e) => setNewAnnouncement(e.currentTarget.value)}
                />
                <Group justify="apart">
                  <Switch
                    label="Pin this announcement"
                    checked={newAnnouncementPinned}
                    onChange={(e) => setNewAnnouncementPinned(e.currentTarget.checked)}
                  />
                  <Button
                    onClick={handleCreateAnnouncement}
                    disabled={!newAnnouncement.trim()}
                  >
                    Post Announcement
                  </Button>
                </Group>
              </Stack>
            </Paper>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}; 